import { useId, useLayoutEffect, type RefObject } from 'react';
import { registerRanges, unregisterRanges } from '@/lib/searchHighlightManager';

function findMatchRanges(container: HTMLElement, searchTerm: string): Range[] {
  const ranges: Range[] = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let node: Text | null;

  while ((node = walker.nextNode() as Text | null)) {
    const text = node.textContent?.toLowerCase() ?? '';
    let startPos = 0;

    while (startPos < text.length) {
      const index = text.indexOf(searchTerm, startPos);
      if (index === -1) break;

      const range = new Range();
      range.setStart(node, index);
      range.setEnd(node, index + searchTerm.length);
      ranges.push(range);

      startPos = index + searchTerm.length;
    }
  }

  return ranges;
}

export function useSearchHighlight(
  ref: RefObject<HTMLElement | null>,
  searchTerm: string,
) {
  const ownerId = useId();

  useLayoutEffect(() => {
    const element = ref.current;
    const trimmed = searchTerm.trim().toLowerCase();

    if (!element || !trimmed) {
      unregisterRanges(ownerId);
      return () => unregisterRanges(ownerId);
    }

    registerRanges(ownerId, findMatchRanges(element, trimmed));

    return () => unregisterRanges(ownerId);
  }, [searchTerm, ref, ownerId]);
}
